// graphClipboardStatusStore — the transient "copied 2 nodes" / "cannot paste
// here" message, as a module singleton rather than React state.
//
// Same shape and the same reason as bakeStatusStore (whose toasts
// BakeNotifications renders): the message must outlive the render churn of the
// surface that produced it. Holding it in the canvas's own useState did not
// survive in practice — the pill appeared with the right text and left the DOM
// ~200 ms later with no second report and no unmount logged, and I did not
// isolate which re-render was eating it. A singleton removes the question
// instead of guessing at it, and matches how every other piece of cross-tree
// state in this repo is held.
//
// SCOPED BY SURFACE ('body' | 'face' | 'events' | a container id). Several graph
// canvases are mounted at once — the hidden ones stay in the DOM rather than
// unmounting — so an unscoped message would render a pill in every one of them,
// and a paste into the event graph would silently also caption the body canvas.
//
// Leaf module, no imports: unit-testable under `node --test`.

/** How long a message stays up. Long enough to read a missing-reference list. */
export const CLIPBOARD_STATUS_MS = 6000

let _status = null           // { surface, level, message, at } | null
let _listeners = []
let _timer = null

// Injectable so tests drive the expiry deterministically instead of sleeping.
let _now = () => Date.now()
let _setTimer = (fn, ms) => setTimeout(fn, ms)
let _clearTimer = (t) => clearTimeout(t)

function notify() {
  for (const fn of _listeners) fn(_status)
}

/**
 * Publish a message for one surface. Replaces whatever that surface (or any
 * other) was showing: one clipboard, one current message.
 */
export function setClipboardStatus(surface, { level = 'info', message } = {}) {
  if (!message) return clearClipboardStatus()
  if (_timer) _clearTimer(_timer)
  _status = { surface, level, message, at: _now() }
  _timer = _setTimer(() => {
    _timer = null
    _status = null
    notify()
  }, CLIPBOARD_STATUS_MS)
  notify()
}

export function clearClipboardStatus() {
  if (_timer) { _clearTimer(_timer); _timer = null }
  if (_status === null) return
  _status = null
  notify()
}

/** The live message, or null. */
export function getClipboardStatus() {
  return _status
}


/** The message for one surface, or null — what a pill renders from. */
export function statusForSurface(surface) {
  return _status && _status.surface === surface ? _status : null
}

/** Subscribe; fires immediately with the current value. */
export function subscribeClipboardStatus(fn) {
  _listeners.push(fn)
  fn(_status)
  return () => { _listeners = _listeners.filter(l => l !== fn) }
}

/** Test seam — reset everything, optionally injecting the clock. */
export function _resetClipboardStatus({ now, setTimer, clearTimer } = {}) {
  if (_timer) _clearTimer(_timer)
  _timer = null
  _status = null
  _listeners = []
  _now = now ?? (() => Date.now())
  _setTimer = setTimer ?? ((fn, ms) => setTimeout(fn, ms))
  _clearTimer = clearTimer ?? ((t) => clearTimeout(t))
}

if (typeof window !== 'undefined') {
  // window.__* convention: an e2e reads the message here rather than racing the
  // DOM for a pill that is deliberately transient.
  window.__GRAPH_CLIPBOARD_STATUS__ = {
    get: getClipboardStatus,
    forSurface: statusForSurface,
    subscribe: subscribeClipboardStatus,
    clear: clearClipboardStatus,
  }
}
